import { trpc } from '@/lib/trpc';
import { type Role, useAuthStore } from '@/store/auth-store';
import { useEffect } from 'react';

// Hydrate the auth-store role from the API. Mount this in AppShell so it
// runs only when authenticated (ProtectedRoute already gates it).
//
// applySession() in auth-store sets a provisional `'operator'` role the
// moment a Supabase session lands. That keeps routing usable for admins
// only after this hook resolves. The window is one round-trip (~100ms).
//
// Mapping: operators.role enum is admin/senior/junior. The frontend
// taxonomy is admin/operator. We collapse senior + junior → 'operator'.
// junior is the default DB value; admins are minted manually.

function mapApiRoleToFront(apiRole: 'admin' | 'senior' | 'junior'): Role {
  return apiRole === 'admin' ? 'admin' : 'operator';
}

export function useHydrateRole(): void {
  const setRole = useAuthStore((s) => s.setRole);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const me = trpc.me.get.useQuery(undefined, {
    enabled: isAuthenticated,
    // Re-fetch every 5 minutes so role changes (e.g. admin promotion) reach
    // the operator's session without forcing a sign-out. Cheap call (~50ms).
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!me.data) return;
    setRole(mapApiRoleToFront(me.data.operator.role));
  }, [me.data, setRole]);
}
